import React, { MouseEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { type TResponsiveProps, type TResponsiveValue, responsiveStyle } from '@hozana/styling/responsive'

import { Cell } from 'elements/layout/Cell'

import { GuideLinksByLang } from 'modules/guide/constants'

import { GuideLink, type TGuideLinkProps } from './GuideLink'

const responsiveProps = { gridTemplateColumns: 'grid-template-columns', w: 'width' } as const

type TGridProps = {
  columns: TResponsiveValue<number>
} & Omit<TResponsiveProps<typeof responsiveProps>, 'gridTemplateColumns'>

const columnsValueToGridTemplate = (columns: number) => Array(columns).fill('1fr').join(' ')
const columnsPropsToGridTemplate = (columns: TGridProps['columns']) =>
  typeof columns === 'object'
    ? Object.entries(columns).reduce((acc, [key, value]) => ({ ...acc, [key]: columnsValueToGridTemplate(value) }), {})
    : columnsValueToGridTemplate(columns)

const Grid = styled.div<TGridProps>`
  display: grid;
  grid-gap: 10px;
  margin: 0 auto;

  ${({ columns, ...otherProps }) =>
    responsiveStyle(responsiveProps)({ gridTemplateColumns: columnsPropsToGridTemplate(columns), ...otherProps })}

  ${({ columns }) =>
    columns === 2 &&
    css`
      &:has(${Cell}:last-of-type:nth-of-type(odd)) ${Cell}:last-of-type {
        grid-column: 1 / -1;
      }
    `}
`

type TGuidesLinkGridProps = { onClick?: MouseEventHandler } & TGridProps & Pick<TGuideLinkProps, 'large'>

export const GuidesLinkGrid: TFC<TGuidesLinkGridProps> = ({ columns = 2, onClick, w = '100%', large }) => {
  const {
    i18n: { language },
  } = useTranslation()

  return (
    <Grid columns={columns} w={w}>
      {GuideLinksByLang[language].map((guideType) => (
        <Cell key={guideType}>
          <GuideLink onClick={onClick} guideType={guideType} large={large} />
        </Cell>
      ))}
    </Grid>
  )
}
